import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, ClipboardCheck, AlertOctagon, Activity, Gauge } from "lucide-react";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatCard } from "../components/shared";
import EntityDrawer from "../components/EntityDrawer";

const FRAMEWORKS = ["ISO 9001", "ISO 14001", "ISO 45001", "ISO 27001", "ISO 50001", "Custom"];
const badge = (s) => ({
  scheduled: "bg-blue-500/10 text-blue-600 dark:text-blue-400", in_progress: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  completed: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400", open: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  closed: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400", major: "bg-red-500/10 text-red-600 dark:text-red-400",
  minor: "bg-amber-500/10 text-amber-600 dark:text-amber-400", observation: "bg-slate-500/10 text-slate-600 dark:text-slate-400",
  mitigated: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
}[s] || "bg-secondary text-muted-foreground");
const ratingColor = (r) => (r >= 15 ? "bg-red-500 text-white" : r >= 8 ? "bg-amber-500 text-white" : "bg-emerald-500 text-white");
const opts = (arr) => arr.map((v) => ({ value: v, label: v.replace("_", " ").replace(/^\w/, (c) => c.toUpperCase()) }));

const DRAWER = {
  audit: { collection: "iso_audits", patchBase: "/iso/audits", title: (i) => i.title, fields: [
    { key: "title", label: "Title", type: "text" },
    { key: "framework", label: "Framework", type: "select", options: FRAMEWORKS.map((f) => ({ value: f, label: f })) },
    { key: "type", label: "Type", type: "select", options: opts(["internal", "external", "inspection"]) },
    { key: "auditor", label: "Auditor", type: "text" },
    { key: "status", label: "Status", type: "select", options: opts(["scheduled", "in_progress", "completed"]) },
    { key: "score", label: "Score (%)", type: "number" },
  ]},
  ncr: { collection: "iso_ncrs", patchBase: "/iso/ncrs", title: (i) => i.title, fields: [
    { key: "title", label: "Title", type: "text" },
    { key: "severity", label: "Severity", type: "select", options: opts(["major", "minor", "observation"]) },
    { key: "status", label: "Status", type: "select", options: opts(["open", "in_progress", "closed"]) },
    { key: "owner", label: "Owner", type: "text" },
    { key: "corrective_action", label: "Corrective action", type: "textarea" },
    { key: "due_date", label: "Due date", type: "date" },
  ]},
  risk: { collection: "iso_risks", patchBase: "/iso/risks", title: (i) => i.description, fields: [
    { key: "description", label: "Description", type: "text" },
    { key: "likelihood", label: "Likelihood (1-5)", type: "select", options: [1,2,3,4,5].map((n) => ({ value: n, label: String(n) })) },
    { key: "severity", label: "Severity (1-5)", type: "select", options: [1,2,3,4,5].map((n) => ({ value: n, label: String(n) })) },
    { key: "owner", label: "Owner", type: "text" },
    { key: "control", label: "Controls", type: "textarea" },
    { key: "status", label: "Status", type: "select", options: opts(["open", "mitigated"]) },
  ]},
};

export default function ISO() {
  const [summary, setSummary] = useState(null);
  const [audits, setAudits] = useState([]);
  const [ncrs, setNcrs] = useState([]);
  const [risks, setRisks] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState({});
  const [sel, setSel] = useState(null);

  const load = async () => {
    const [s, a, n, r] = await Promise.all([api.get("/iso/summary"), api.get("/iso/audits"), api.get("/iso/ncrs"), api.get("/iso/risks")]);
    setSummary(s.data); setAudits(a.data); setNcrs(n.data); setRisks(r.data);
  };
  useEffect(() => { load(); }, []);

  const open = (type) => {
    setForm(type === "audit" ? { framework: "ISO 9001", type: "internal", status: "scheduled" }
      : type === "ncr" ? { severity: "minor", status: "open" } : { likelihood: "3", severity: "3", status: "open" });
    setDlg(type);
  };
  const save = async () => {
    try {
      if (dlg === "audit") await api.post("/iso/audits", form);
      if (dlg === "ncr") await api.post("/iso/ncrs", form);
      if (dlg === "risk") await api.post("/iso/risks", { ...form, likelihood: Number(form.likelihood), severity: Number(form.severity) });
      toast.success("Created"); setDlg(null); load();
    } catch { toast.error("Failed to save"); }
  };
  const onSaved = () => load();

  if (!summary) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;

  const Row = ({ children, onClick, testid }) => (
    <div onClick={onClick} data-testid={testid} className="flex cursor-pointer flex-wrap items-center justify-between gap-2 border-b border-border/60 px-5 py-3.5 transition-colors last:border-0 hover:bg-secondary/50">{children}</div>
  );

  return (
    <div>
      <PageHeader title="ISO Compliance" subtitle="Click any entry to edit details, change status and add notes." />
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={ClipboardCheck} label="Audits completed" value={`${summary.audits_completed}/${summary.audits_total}`} />
        <StatCard icon={AlertOctagon} label="Open non-conformances" value={summary.ncrs_open} tone="bg-red-500/10 text-red-600 dark:text-red-400" />
        <StatCard icon={Activity} label="High risks" value={summary.risks_high} tone="bg-amber-500/10 text-amber-600 dark:text-amber-400" />
        <StatCard icon={Gauge} label="Avg audit score" value={`${summary.avg_score}%`} tone="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" />
      </div>

      <Tabs defaultValue="audits">
        <TabsList className="rounded-full">
          <TabsTrigger value="audits" className="rounded-full" data-testid="iso-tab-audits">Audits</TabsTrigger>
          <TabsTrigger value="ncrs" className="rounded-full" data-testid="iso-tab-ncrs">Non-Conformances</TabsTrigger>
          <TabsTrigger value="risks" className="rounded-full" data-testid="iso-tab-risks">Risk Register</TabsTrigger>
        </TabsList>

        <TabsContent value="audits" className="mt-4">
          <div className="mb-3 flex justify-end"><Button className="rounded-full" onClick={() => open("audit")} data-testid="new-audit-btn"><Plus className="mr-2 h-4 w-4" /> New Audit</Button></div>
          <Card className="overflow-hidden rounded-2xl border-border">
            {audits.map((a) => (
              <Row key={a.id} onClick={() => setSel({ type: "audit", item: a })} testid={`audit-row-${a.id}`}>
                <div><div className="text-sm font-medium">{a.title}</div><div className="text-xs text-muted-foreground">{a.framework} · {a.type} · {a.auditor}</div></div>
                <div className="flex items-center gap-2">
                  {a.score && <Badge variant="secondary" className="rounded-full">{a.score}%</Badge>}
                  {a.notes?.length > 0 && <Badge variant="secondary" className="rounded-full">{a.notes.length} notes</Badge>}
                  <Badge className={`rounded-full capitalize ${badge(a.status)}`}>{(a.status || "").replace("_", " ")}</Badge>
                </div>
              </Row>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="ncrs" className="mt-4">
          <div className="mb-3 flex justify-end"><Button className="rounded-full" onClick={() => open("ncr")} data-testid="new-ncr-btn"><Plus className="mr-2 h-4 w-4" /> New NCR</Button></div>
          <Card className="overflow-hidden rounded-2xl border-border">
            {ncrs.map((n) => (
              <Row key={n.id} onClick={() => setSel({ type: "ncr", item: n })} testid={`ncr-row-${n.id}`}>
                <div><div className="text-sm font-medium">{n.title}</div><div className="text-xs text-muted-foreground">Owner: {n.owner}</div></div>
                <div className="flex items-center gap-2">
                  <Badge className={`rounded-full capitalize ${badge(n.severity)}`}>{n.severity}</Badge>
                  <Badge className={`rounded-full capitalize ${badge(n.status)}`}>{(n.status || "").replace("_", " ")}</Badge>
                </div>
              </Row>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="risks" className="mt-4">
          <div className="mb-3 flex justify-end"><Button className="rounded-full" onClick={() => open("risk")} data-testid="new-risk-btn"><Plus className="mr-2 h-4 w-4" /> New Risk</Button></div>
          <Card className="overflow-hidden rounded-2xl border-border">
            {risks.map((r) => (
              <Row key={r.id} onClick={() => setSel({ type: "risk", item: r })} testid={`risk-row-${r.id}`}>
                <div className="flex items-center gap-3">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg font-heading text-sm font-bold ${ratingColor(r.rating)}`}>{r.rating}</div>
                  <div><div className="text-sm font-medium">{r.description}</div><div className="text-xs text-muted-foreground">L{r.likelihood} × S{r.severity} · {r.owner}</div></div>
                </div>
                <Badge className={`rounded-full capitalize ${badge(r.status)}`}>{r.status}</Badge>
              </Row>
            ))}
          </Card>
        </TabsContent>
      </Tabs>

      {sel && (
        <EntityDrawer open={!!sel} onClose={() => setSel(null)} title={DRAWER[sel.type].title(sel.item)}
          collection={DRAWER[sel.type].collection} patchBase={DRAWER[sel.type].patchBase}
          fields={DRAWER[sel.type].fields} item={sel.item} onSaved={onSaved} />
      )}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading capitalize">New {dlg === "ncr" ? "Non-Conformance" : dlg}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {dlg === "audit" && (<>
              <div className="space-y-1.5"><Label>Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="audit-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Framework</Label><Select value={form.framework} onValueChange={(v) => setForm({ ...form, framework: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{FRAMEWORKS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5"><Label>Type</Label><Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="internal">Internal</SelectItem><SelectItem value="external">External</SelectItem><SelectItem value="inspection">Inspection</SelectItem></SelectContent></Select></div>
              </div>
              <div className="space-y-1.5"><Label>Auditor</Label><Input value={form.auditor || ""} onChange={(e) => setForm({ ...form, auditor: e.target.value })} /></div>
            </>)}
            {dlg === "ncr" && (<>
              <div className="space-y-1.5"><Label>Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ncr-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Severity</Label><Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="major">Major</SelectItem><SelectItem value="minor">Minor</SelectItem><SelectItem value="observation">Observation</SelectItem></SelectContent></Select></div>
                <div className="space-y-1.5"><Label>Owner</Label><Input value={form.owner || ""} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></div>
              </div>
            </>)}
            {dlg === "risk" && (<>
              <div className="space-y-1.5"><Label>Description</Label><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="risk-desc" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Likelihood</Label><Select value={form.likelihood} onValueChange={(v) => setForm({ ...form, likelihood: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{[1,2,3,4,5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5"><Label>Severity</Label><Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{[1,2,3,4,5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="space-y-1.5"><Label>Owner</Label><Input value={form.owner || ""} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></div>
            </>)}
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full" data-testid="iso-save-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
